import { Button, FormControl, Grid, SvgIcon, TextField } from '@mui/material';
import CountZonesTable from './CountZonesTable';
import { CountZonesResponse } from '../../../app/services/api/generated';
import { PlusCircle } from '../../../assets';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../../../components/shared/useUser';

const MainBox = (props: { zones: CountZonesResponse[], storeID: string, loadData: (itemNumber?: string) => void, isLoading: boolean }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [activeItemSearch, setActiveItemSearch] = useState('');
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);
  const hasRightToEdit: boolean = hasPermissionTo(['StockLocationWrite']) || hasPermissionTo(['StockCountAdmin']);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleItemSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setItemSearchTerm(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const trimmed = value.trim();
      setActiveItemSearch(trimmed);
      props.loadData(trimmed || undefined);
    }, 300);
  };

  const handleClearItemSearch = useCallback(() => {
    setItemSearchTerm('');
    setActiveItemSearch('');
    props.loadData();
  }, [props.loadData]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <Grid container
      sx={{ pt: '8px' }}>
      <Grid item
        xs={8}
        sx={{ pb: '8px', display: 'flex', gap: 2 }}>
        <FormControl>
          <TextField
            label={t('searchByZone')}
            size='small'
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </FormControl>
        <FormControl>
          <TextField
            label={t('searchByItem')}
            size='small'
            value={itemSearchTerm}
            onChange={handleItemSearchChange}
          />
        </FormControl>
      </Grid>
      <Grid item
        xs={4}
        sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          size='lg'
          sx={{ ml: 4 }}
          onClick={() => navigate('/create-edit-count-zone')}
          startIcon={<SvgIcon><PlusCircle /></SvgIcon>}
          disabled={!hasRightToEdit}
        >
          {t('createNew')}
        </Button>
      </Grid>
      <Grid item
        xs={12}>
        <CountZonesTable
          list={props.zones}
          storeID={props.storeID}
          loadData={props.loadData}
          searchTerm={searchTerm}
          isLoading={props.isLoading}
          itemSearchTerm={activeItemSearch}
          onClearItemSearch={handleClearItemSearch} />
      </Grid>
    </Grid>
  );
};

const CountZonesBox = (props: { zones: CountZonesResponse[], loadData: (itemNumber?: string) => void, storeID: string, isLoading: boolean }) => {
  return (
    <MainBox zones={props.zones}
      storeID={props.storeID}
      loadData={props.loadData}
      isLoading={props.isLoading} />
  );
};

export default CountZonesBox;
